import React from "react";
import { Link } from "react-router-dom";
import { Mail, Building2, ArrowLeft } from "lucide-react";
import { useI18n } from "@/i18n";

// Single source of truth for legal/company identity across public legal pages.
export const COMPANY = {
  legal: "SDIP TECHNOLOGIES LTD",
  number: "17478518",
  jurisdictionEn: "Registered in England and Wales",
  jurisdictionTr: "İngiltere ve Galler'de tescillidir",
  office: ["Suite 11245", "5 Brayford Square", "London", "United Kingdom", "E1 0SG"],
  general: "hello@sdiptechnologies.com",
  billing: "commerce@sdiptechnologies.com",
};

const UPDATED_EN = "June 2026";
const UPDATED_TR = "Haziran 2026";

function LegalShell({ title, updated, children }: { title: string; updated?: string; children: React.ReactNode }) {
  const { lang } = useI18n();
  const jurisdiction = lang === "tr" ? COMPANY.jurisdictionTr : COMPANY.jurisdictionEn;
  const companyNo = lang === "tr" ? "Şirket No." : "Company No.";
  const lastUpdated = lang === "tr" ? "Son güncelleme" : "Last updated";
  return (
    <main className="mx-auto max-w-3xl px-4 py-16 lg:px-8" data-testid="legal-page">
      <Link to="/" className="mb-8 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> {lang === "tr" ? "Ana sayfa" : "Home"}
      </Link>
      <h1 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">{title}</h1>
      <p className="mt-2 text-sm text-muted-foreground">{COMPANY.legal} · {companyNo} {COMPANY.number} · {jurisdiction}{updated ? ` · ${lastUpdated} ${updated}` : ""}</p>
      <div className="legal-body mt-8 space-y-5 text-sm leading-relaxed text-foreground/85 [&_h2]:mt-8 [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:text-foreground [&_ul]:list-disc [&_ul]:space-y-1.5 [&_ul]:pl-5 [&_a]:text-atlas-300 [&_a]:underline">
        {children}
      </div>
      <div className="mt-12 rounded-2xl border border-line bg-white/[0.02] p-5 text-sm text-muted-foreground">
        <p className="font-semibold text-foreground">{COMPANY.legal}</p>
        <p>{companyNo} {COMPANY.number} · {jurisdiction}</p>
        <p className="mt-1">{lang === "tr" ? "Kayıtlı ofis" : "Registered office"}: {COMPANY.office.join(", ")}</p>
        <p className="mt-2">{lang === "tr" ? "Genel" : "General"}: <a href={`mailto:${COMPANY.general}`} className="text-atlas-300 underline">{COMPANY.general}</a> · {lang === "tr" ? "Faturalandırma" : "Billing"}: <a href={`mailto:${COMPANY.billing}`} className="text-atlas-300 underline">{COMPANY.billing}</a></p>
      </div>
    </main>
  );
}

export function Terms() {
  const { lang } = useI18n();
  if (lang === "tr") {
    return (
      <LegalShell title="Hizmet Şartları" updated={UPDATED_TR}>
        <p>Bu Hizmet Şartları ("Şartlar"), {COMPANY.legal} ("SDIP Technologies", "biz") tarafından işletilen SDIP AI'ya ("Hizmet") erişiminizi ve bunu kullanımınızı düzenler. Bir hesap oluşturarak veya Hizmet'i kullanarak bu Şartları kabul etmiş olursunuz.</p>
        <h2>1. Hizmet</h2>
        <p>SDIP AI; ATLAS zekâ motoru ve ATLAS Asistan aracılığıyla yapay zekâ destekli futbol analitiği, canlı maç bağlamı, maç öncesi analiz, istatistiksel karar desteği ve açıklanabilir içgörüler sunan abonelik tabanlı bir yazılımdır. Yazılıma, veri yorumlamaya ve yapay zekâ destekli futbol zekâsına erişim için ödeme yaparsınız.</p>
        <p>SDIP AI bahis kabul etmez veya kolaylaştırmaz, bahis oynamaz, bir bahisçi veya borsa olarak faaliyet göstermez, müşteri fonu tutmaz, para yatırma veya çekme işlemi yapmaz ve ödül ödemez. Sportif sonuçları veya finansal getirileri garanti etmez.</p>
        <h2>2. Hesaplar</h2>
        <p>Hesap kimlik bilgilerinizin gizliliğini korumaktan ve hesabınız altındaki tüm faaliyetlerden siz sorumlusunuz. Doğru bilgi vermeli ve değiştiğinde hemen güncellemelisiniz.</p>
        <h2>3. Kabul edilebilir kullanım</h2>
        <ul>
          <li>Hizmet'i kötüye kullanmayın, aksatmayın, tersine mühendislik yapmayın veya yetkisiz erişim elde etmeye çalışmayın.</li>
          <li>Açıkça izin verilmedikçe verileri veya analitiği kazımayın, yeniden satmayın veya yeniden dağıtmayın.</li>
          <li>Hizmet'i yasa dışı amaçlarla veya geçerli yasaları ihlal edecek şekilde kullanmayın.</li>
        </ul>
        <h2>4. Fikri mülkiyet</h2>
        <p>Yazılım, modeller, içerik ve SDIP ile ATLAS markaları dâhil Hizmet, SDIP Technologies'e aittir veya lisanslanmıştır ve yasalarla korunur. Bu Şartlar size Hizmet'i amacına uygun kullanmak için sınırlı, münhasır olmayan ve devredilemez bir hak tanır.</p>
        <h2>5. Abonelikler ve faturalandırma</h2>
        <p>Ücretli abonelikler (PRO ve ELITE), seçilen aralıkta (aylık veya yıllık) GBP cinsinden peşin faturalanır ve iptal edilene kadar otomatik olarak yenilenir. Gerekli durumlarda ilgili vergiler eklenebilir. Ücretli kart ödemesi etkinleştirildiğinde ödemeler, ödeme hizmet sağlayıcımız tarafından işlenir.</p>
        <h2>6. İptal ve iade</h2>
        <p>Gelecekteki yenilemeleri istediğiniz zaman iptal edebilirsiniz; <Link to="/cancellation">İptal Politikası</Link> ve <Link to="/refunds">İade ve İtiraz Politikası</Link>'na bakın.</p>
        <h2>7. Hizmet erişilebilirliği ve veri sınırlamaları</h2>
        <p>Güvenilir bir Hizmet sunmayı amaçlıyoruz ancak kesintisiz erişilebilirlik garanti etmiyoruz. Futbol verileri ve model çıktıları eksik, gecikmeli, belirsiz veya mevcut olmayabilir; analiz yalnızca bilgi ve karar desteği amacıyla sunulur ve herhangi bir sportif veya finansal sonuç garantisi vermez.</p>
        <h2>8. Sorumluluğun sınırlandırılması</h2>
        <p>Yasaların izin verdiği azami ölçüde, SDIP Technologies dolaylı, arızi veya sonuç olarak ortaya çıkan zararlardan ya da Hizmet'e dayanarak verdiğiniz kararlardan sorumlu değildir. Bu Şartlardaki hiçbir hüküm, belirli yasal tüketici hakları dâhil, yasal olarak hariç tutulamayan veya sınırlandırılamayan hakları hariç tutmaz ya da sınırlamaz.</p>
        <h2>9. Askıya alma ve fesih</h2>
        <p>Bu Şartların ihlali veya Hizmet'in kötüye kullanılması durumunda erişimi askıya alabilir veya sonlandırabiliriz.</p>
        <h2>10. Değişiklikler</h2>
        <p>Bu Şartları zaman zaman güncelleyebiliriz. Önemli değişiklikler, güncellenmiş bir tarihle ve uygun olduğunda ek bildirimle yansıtılır.</p>
        <h2>11. Geçerli hukuk</h2>
        <p>Bu Şartlar İngiltere ve Galler yasalarına tabidir ve İngiltere ve Galler mahkemeleri, ikamet ettiğiniz ülkedeki zorunlu tüketici korumalarını etkilemeksizin yargı yetkisine sahiptir.</p>
        <h2>12. İletişim</h2>
        <p>Bu Şartlarla ilgili sorular: <a href={`mailto:${COMPANY.general}`}>{COMPANY.general}</a>.</p>
      </LegalShell>
    );
  }
  return (
    <LegalShell title="Terms of Service" updated={UPDATED_EN}>
      <p>These Terms of Service ("Terms") govern your access to and use of SDIP AI (the "Service"), operated by {COMPANY.legal} ("SDIP Technologies", "we", "us"). By creating an account or using the Service you agree to these Terms.</p>
      <h2>1. The Service</h2>
      <p>SDIP AI is subscription software providing AI-powered football analytics, live match context, prematch analysis, statistical decision support and explainable insights through the ATLAS intelligence engine and ATLAS Assistant. You pay for access to software, data interpretation and AI-assisted football intelligence.</p>
      <p>SDIP AI does not accept or facilitate wagers, place bets, operate as a bookmaker or exchange, hold customer funds, process deposits or withdrawals, or pay prizes. It does not guarantee sporting outcomes or financial returns.</p>
      <h2>2. Accounts</h2>
      <p>You are responsible for maintaining the confidentiality of your account credentials and for all activity under your account. You must provide accurate information and promptly update it when it changes.</p>
      <h2>3. Acceptable use</h2>
      <ul>
        <li>Do not misuse, disrupt, reverse engineer, or attempt to gain unauthorised access to the Service.</li>
        <li>Do not scrape, resell, or redistribute data or analytics except as expressly permitted.</li>
        <li>Do not use the Service for unlawful purposes or in breach of applicable laws.</li>
      </ul>
      <h2>4. Intellectual property</h2>
      <p>The Service, including software, models, content and the SDIP and ATLAS marks, is owned by or licensed to SDIP Technologies and protected by law. These Terms grant you a limited, non-exclusive, non-transferable right to use the Service for its intended purpose.</p>
      <h2>5. Subscriptions and billing</h2>
      <p>Paid subscriptions (PRO and ELITE) are billed in advance at the selected interval (monthly or yearly) in GBP, and renew automatically until cancelled. Applicable taxes may be added where required. When paid card billing is enabled, payments are processed by our payment processor.</p>
      <h2>6. Cancellation and refunds</h2>
      <p>You may cancel future renewals at any time; see the <Link to="/cancellation">Cancellation Policy</Link> and <Link to="/refunds">Refund &amp; Dispute Policy</Link>.</p>
      <h2>7. Service availability and data limitations</h2>
      <p>We aim to provide a reliable Service but do not guarantee uninterrupted availability. Football data and model output may be incomplete, delayed, uncertain or unavailable, and analysis is provided for information and decision support only, without any guarantee of sporting or financial outcomes.</p>
      <h2>8. Limitation of liability</h2>
      <p>To the maximum extent permitted by law, SDIP Technologies is not liable for indirect, incidental or consequential losses, or for decisions you make based on the Service. Nothing in these Terms excludes or limits rights that cannot lawfully be excluded or limited, including certain statutory consumer rights.</p>
      <h2>9. Suspension and termination</h2>
      <p>We may suspend or terminate access for breach of these Terms or misuse of the Service.</p>
      <h2>10. Changes</h2>
      <p>We may update these Terms from time to time. Material changes will be reflected by an updated date and, where appropriate, further notice.</p>
      <h2>11. Governing law</h2>
      <p>These Terms are governed by the laws of England and Wales, and the courts of England and Wales have jurisdiction, without affecting mandatory consumer protections in your country of residence.</p>
      <h2>12. Contact</h2>
      <p>Questions about these Terms: <a href={`mailto:${COMPANY.general}`}>{COMPANY.general}</a>.</p>
    </LegalShell>
  );
}

export function Privacy() {
  const { lang } = useI18n();
  if (lang === "tr") {
    return (
      <LegalShell title="Gizlilik Politikası" updated={UPDATED_TR}>
        <p>{COMPANY.legal} ("biz") gizliliğinize saygı duyar. Bu politika, SDIP AI'yı kullandığınızda işlediğimiz kişisel verileri ve haklarınızı açıklar.</p>
        <h2>İşleyebileceğimiz veriler</h2>
        <ul>
          <li>Hesap kimlik bilgileri ve e-posta adresi.</li>
          <li>Hesap ve üyelik/abonelik bilgileri.</li>
          <li>Uygun olduğunda ürün kullanım bilgileri ve tercihler (ör. favoriler).</li>
          <li>Teknik ve cihaz bilgileri ile güvenlik günlükleri.</li>
          <li>Müşteri destek iletişimleri.</li>
          <li>Ücretli abonelikler etkinleştirildiğinde ödemeyle ilgili üst veriler.</li>
        </ul>
        <h2>Amaçlar ve hukuki dayanaklar</h2>
        <p>Verileri; Hizmet'i sağlamak ve güvenliğini sağlamak (sözleşmenin ifası), geliştirmek, desteklemek ve kötüye kullanımı önlemek (meşru menfaatler), faturalandırmayı yürütmek (sözleşme ve yasal yükümlülük) ve rıza geçerli olduğunda onayladığınız iletişimler için işleriz.</p>
        <h2>Ödemeler</h2>
        <p>Ücretli kart ödemesi etkinleştirildiğinde kart ödemeleri, ödeme hizmet sağlayıcımız tarafından işlenir. SDIP Technologies'in tam kart ödeme ayrıntılarını kendi sistemlerinde saklamasına gerek yoktur.</p>
        <h2>Hizmet sağlayıcılar</h2>
        <p>Uygun güvenceler altında bizim adımıza veri işleyen güvenilir hizmet sağlayıcılar (ör. barındırma, altyapı ve etkinleştirildiğinde ödeme işleme) kullanırız.</p>
        <h2>Güvenlik ve saklama</h2>
        <p>Verileri korumak için makul teknik ve organizasyonel önlemler uygular ve yalnızca açıklanan amaçlar için gerekli olduğu sürece ya da yasanın gerektirdiği kadar saklarız.</p>
        <h2>Uluslararası işleme</h2>
        <p>Verilerin ülkeniz dışında işlendiği durumlarda, geçerli yasalarla tutarlı uygun güvenceler uygularız.</p>
        <h2>Haklarınız</h2>
        <p>Geçerli yasalara tabi olarak, verilerinize erişim, düzeltme, silme, kısıtlama veya taşınabilirlik talep edebilir ve belirli işlemelere itiraz edebilirsiniz. Bu hakları kullanmak için <a href={`mailto:${COMPANY.general}`}>{COMPANY.general}</a> ile iletişime geçin.</p>
        <h2>İletişim</h2>
        <p>{COMPANY.legal}, {COMPANY.office.join(", ")}. E-posta <a href={`mailto:${COMPANY.general}`}>{COMPANY.general}</a>.</p>
      </LegalShell>
    );
  }
  return (
    <LegalShell title="Privacy Policy" updated={UPDATED_EN}>
      <p>{COMPANY.legal} ("we") respects your privacy. This policy explains what personal data we process when you use SDIP AI and your rights.</p>
      <h2>Data we may process</h2>
      <ul>
        <li>Account identity information and email address.</li>
        <li>Account and membership/subscription information.</li>
        <li>Product usage information and preferences (e.g. favourites) where applicable.</li>
        <li>Technical and device information and security logs.</li>
        <li>Customer support communications.</li>
        <li>Payment-related metadata once paid subscriptions are enabled.</li>
      </ul>
      <h2>Purposes and legal bases</h2>
      <p>We process data to provide and secure the Service (performance of a contract), to improve and support it and prevent abuse (legitimate interests), to handle billing (contract and legal obligation), and for communications you have consented to where consent applies.</p>
      <h2>Payments</h2>
      <p>When paid card billing is enabled, card payments are processed by our payment processor. SDIP Technologies does not need to store full payment-card details on its own systems.</p>
      <h2>Service providers</h2>
      <p>We use trusted processors (e.g. hosting, infrastructure and, when enabled, payment processing) that handle data on our behalf under appropriate safeguards.</p>
      <h2>Security and retention</h2>
      <p>We apply reasonable technical and organisational measures to protect data and retain it only as long as necessary for the purposes described or as required by law.</p>
      <h2>International processing</h2>
      <p>Where data is processed outside your country, we use appropriate safeguards consistent with applicable law.</p>
      <h2>Your rights</h2>
      <p>Subject to applicable law, you may request access, correction, deletion, restriction or portability of your data, and object to certain processing. To exercise these rights, contact <a href={`mailto:${COMPANY.general}`}>{COMPANY.general}</a>.</p>
      <h2>Contact</h2>
      <p>{COMPANY.legal}, {COMPANY.office.join(", ")}. Email <a href={`mailto:${COMPANY.general}`}>{COMPANY.general}</a>.</p>
    </LegalShell>
  );
}

export function Refunds() {
  const { lang } = useI18n();
  if (lang === "tr") {
    return (
      <LegalShell title="İade ve İtiraz Politikası" updated={UPDATED_TR}>
        <p>Bu politika, {COMPANY.legal} tarafından işletilen SDIP AI abonelikleri için iadeleri açıklar.</p>
        <ul>
          <li>FREE planında abonelik ücreti yoktur.</li>
          <li>İlk ücretli abonelik ücretinizin iadesini, satın alımdan sonraki 14 gün içinde <a href={`mailto:${COMPANY.billing}`}>{COMPANY.billing}</a> adresine e-posta göndererek talep edebilirsiniz.</li>
          <li>Mükerrer ücretlendirmeler ve teyit edilen teknik faturalandırma hataları düzeltilir veya iade edilir.</li>
          <li>İlk iade süresinden sonra, ilgili faturalandırma dönemi başladıktan sonra abonelik ücretleri, geçerli yasanın gerektirdiği durumlar veya SDIP Technologies'in aksini kabul ettiği durumlar dışında genellikle iade edilmez.</li>
          <li>Onaylanan iadeler mümkün olduğunda orijinal ödeme yöntemine geri yapılır; işlem süresi ödeme sağlayıcısına veya ihraç eden bankaya bağlı olabilir.</li>
        </ul>
        <p>İadeler yasal tüketici haklarınızı etkilemez ve bu politika, geçerli yasa kapsamında mevcut zorunlu tüketici haklarını sınırlamaz. Herhangi bir faturalandırma sorunu için <a href={`mailto:${COMPANY.billing}`}>{COMPANY.billing}</a> ile iletişime geçin.</p>
      </LegalShell>
    );
  }
  return (
    <LegalShell title="Refund & Dispute Policy" updated={UPDATED_EN}>
      <p>This policy explains refunds for SDIP AI subscriptions operated by {COMPANY.legal}.</p>
      <ul>
        <li>The FREE plan carries no subscription charge.</li>
        <li>You may request a refund of your initial paid subscription charge within 14 days of purchase by emailing <a href={`mailto:${COMPANY.billing}`}>{COMPANY.billing}</a>.</li>
        <li>Duplicate charges and confirmed technical billing errors will be corrected or refunded.</li>
        <li>After the initial refund period, subscription charges are generally non-refundable once the relevant billing period has started, except where required by applicable law or where SDIP Technologies agrees otherwise.</li>
        <li>Approved refunds are returned to the original payment method where possible; processing time may depend on the payment provider or issuing bank.</li>
      </ul>
      <p>Refunds do not affect your statutory consumer rights, and this policy does not limit any mandatory consumer rights available under applicable law. For any billing concern, contact <a href={`mailto:${COMPANY.billing}`}>{COMPANY.billing}</a>.</p>
    </LegalShell>
  );
}

export function Cancellation() {
  const { lang } = useI18n();
  if (lang === "tr") {
    return (
      <LegalShell title="İptal Politikası" updated={UPDATED_TR}>
        <ul>
          <li>Ücretli abonelikler iptal edilmedikçe otomatik olarak yenilenir.</li>
          <li>Gelecekteki yenilemeyi istediğiniz zaman iptal edebilirsiniz; iptal, bir sonraki yenileme ücretini engeller.</li>
          <li>Yasa veya aksi belirtilmedikçe, erişim mevcut ücretli faturalandırma döneminin sonuna kadar kullanılabilir kalır.</li>
          <li>İptal normalde halihazırda kullanılan süre için orantılı bir iade oluşturmaz; iade hakları <Link to="/refunds">İade ve İtiraz Politikası</Link> ile düzenlenir.</li>
          <li>İptali <a href={`mailto:${COMPANY.billing}`}>{COMPANY.billing}</a> adresinden talep edebilirsiniz. Self servis faturalandırma yönetimi kullanılabilir olduğunda, hesap faturalandırma kontrollerinizi de kullanabilirsiniz.</li>
        </ul>
      </LegalShell>
    );
  }
  return (
    <LegalShell title="Cancellation Policy" updated={UPDATED_EN}>
      <ul>
        <li>Paid subscriptions renew automatically unless cancelled.</li>
        <li>You may cancel future renewal at any time; cancellation prevents the next renewal charge.</li>
        <li>Unless otherwise required by law or stated, access remains available until the end of the current paid billing period.</li>
        <li>Cancellation does not normally create a prorated refund for time already used; refund rights are governed by the <Link to="/refunds">Refund &amp; Dispute Policy</Link>.</li>
        <li>You may request cancellation at <a href={`mailto:${COMPANY.billing}`}>{COMPANY.billing}</a>. When self-service billing management is available, you may also use your account billing controls.</li>
      </ul>
    </LegalShell>
  );
}

export function ResponsibleUse() {
  const { lang } = useI18n();
  if (lang === "tr") {
    return (
      <LegalShell title="Sorumlu Kullanım" updated={UPDATED_TR}>
        <p>SDIP AI, futbol analitiği ve karar destek yazılımıdır.</p>
        <ul>
          <li>SDIP AI bahis, para yatırma, para çekme veya ödül ödemelerini kabul etmez ya da kolaylaştırmaz.</li>
          <li>SDIP AI maç sonuçlarını veya finansal getirileri garanti etmez.</li>
          <li>Futbol verileri ve model çıktıları eksik, gecikmeli, belirsiz veya mevcut olmayabilir.</li>
          <li>Kullanıcılar, platformun sağladığı bilgileri nasıl kullandıklarından ve kendi yargı bölgelerindeki geçerli yasalara uymaktan sorumludur.</li>
        </ul>
        <p>Yalnızca analiz ve karar desteği. Garantili sonuç yoktur.</p>
      </LegalShell>
    );
  }
  return (
    <LegalShell title="Responsible Use" updated={UPDATED_EN}>
      <p>SDIP AI is football analytics and decision-support software.</p>
      <ul>
        <li>SDIP AI does not accept or facilitate wagers, deposits, withdrawals or prize payments.</li>
        <li>SDIP AI does not guarantee match outcomes or financial returns.</li>
        <li>Football data and model output can be incomplete, delayed, uncertain or unavailable.</li>
        <li>Users remain responsible for how they use information provided by the platform and for compliance with applicable laws in their jurisdiction.</li>
      </ul>
      <p>Analysis and decision support only. No guaranteed outcomes.</p>
    </LegalShell>
  );
}

export function Contact() {
  const { lang } = useI18n();
  const companyNo = lang === "tr" ? "Şirket No." : "Company No.";
  const jurisdiction = lang === "tr" ? COMPANY.jurisdictionTr : COMPANY.jurisdictionEn;
  return (
    <main className="mx-auto max-w-3xl px-4 py-16 lg:px-8" data-testid="contact-page">
      <Link to="/" className="mb-8 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> {lang === "tr" ? "Ana sayfa" : "Home"}
      </Link>
      <h1 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">{lang === "tr" ? "İletişim" : "Contact"}</h1>
      <p className="mt-3 max-w-xl text-sm text-muted-foreground">{lang === "tr" ? "Müşteri ve faturalandırma taleplerine makul ölçüde en kısa sürede yanıt vermeyi amaçlıyoruz." : "We aim to respond to customer and billing enquiries as promptly as reasonably possible."}</p>
      <div className="mt-8 grid gap-4 sm:grid-cols-2">
        <a href={`mailto:${COMPANY.general}`} data-testid="contact-general" className="group rounded-2xl border border-line bg-white/[0.02] p-5 transition hover:border-atlas/30">
          <Mail className="h-5 w-5 text-atlas-400" />
          <p className="mt-3 text-sm font-semibold text-foreground">{lang === "tr" ? "Genel destek" : "General support"}</p>
          <p className="text-sm text-atlas-300 underline">{COMPANY.general}</p>
        </a>
        <a href={`mailto:${COMPANY.billing}`} data-testid="contact-billing" className="group rounded-2xl border border-line bg-white/[0.02] p-5 transition hover:border-atlas/30">
          <Mail className="h-5 w-5 text-sdip-green" />
          <p className="mt-3 text-sm font-semibold text-foreground">{lang === "tr" ? "Faturalandırma ve abonelik" : "Billing & subscriptions"}</p>
          <p className="text-sm text-atlas-300 underline">{COMPANY.billing}</p>
        </a>
      </div>
      <div className="mt-6 flex items-start gap-3 rounded-2xl border border-line bg-white/[0.02] p-5">
        <Building2 className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
        <div className="text-sm text-muted-foreground">
          <p className="font-semibold text-foreground">{COMPANY.legal}</p>
          <p>{companyNo} {COMPANY.number} · {jurisdiction}</p>
          <p className="mt-1">{lang === "tr" ? "Kayıtlı ofis" : "Registered office"}: {COMPANY.office.join(", ")}</p>
        </div>
      </div>
      <p className="mt-6 text-xs text-muted-foreground">{lang === "tr" ? "SDIP AI, futbol analitiği ve karar destek yazılımı sunar. Bahis kabul etmez, bahis oynamaz, müşteri fonu tutmaz, ödül ödemez veya sportif ya da finansal sonuçları garanti etmez." : "SDIP AI provides football analytics and decision-support software. It does not accept wagers, place bets, hold customer funds, pay prizes, or guarantee sporting or financial outcomes."}</p>
    </main>
  );
}
